/**
 * Phase 2 transition execution modal — upgraded in Phase 2.5 (option A)
 * to render the existing DynamicMetadataForm when a transition declares
 * a formMetadataSchemaId. The JSON textarea now appears ONLY as a
 * fallback when the schema fetch fails, so operators get a proper
 * field-by-field form for damage reports / sterilization records /
 * approved-delivery photos / etc.
 *
 * No backend change required — the MetadataSchema endpoints already
 * back the existing AssetDetailPage form. We reuse them verbatim.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowRight, Code2 } from 'lucide-react';
import { toast } from 'sonner';
import { useExecuteTransition, type ValidTransition } from '@/api/hooks/useWorkflows';
import { useMetadataSchema } from '@/api/hooks/useMetadata';
import DynamicMetadataForm, {
  DynamicMetadataFormSkeleton,
} from '@/components/metadata/DynamicMetadataForm';
import type { MetadataValue } from '@/types/metadata.types';

interface Props {
  assetId: string;
  transition: ValidTransition | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful transition; the parent typically refreshes data. */
  onSuccess?: () => void;
}

export function TransitionDialog({
  assetId,
  transition,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const { t } = useTranslation('workflows');
  const schemaId = transition?.formMetadataSchemaId ?? null;
  const schemaQuery = useMetadataSchema(schemaId ?? 0, schemaId != null);

  const [values, setValues] = useState<MetadataValue[]>([]);
  const [formValid, setFormValid] = useState(true);
  const [rawJson, setRawJson] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const execute = useExecuteTransition();

  // Reset state when the picked transition changes.
  useEffect(() => {
    setValues([]);
    setFormValid(true);
    setRawJson('');
    setShowRaw(false);
  }, [transition?.code, schemaId, open]);

  const hasSchema = schemaId != null;
  const schemaFetchFailed =
    hasSchema && !schemaQuery.isLoading && !schemaQuery.data;
  const useRawTextarea = !hasSchema ? false : showRaw || schemaFetchFailed;

  // Build the JSON payload for the engine: { fieldKey: value, ... }.
  const payload = useMemo(() => {
    if (!hasSchema) return null;
    if (useRawTextarea) {
      if (!rawJson.trim()) return {};
      try {
        return JSON.parse(rawJson);
      } catch {
        return undefined; // signals invalid JSON
      }
    }
    const obj: Record<string, unknown> = {};
    for (const v of values) {
      obj[v.fieldKey] = v.value;
    }
    return obj;
  }, [hasSchema, useRawTextarea, rawJson, values]);

  const handleSubmit = async () => {
    if (!transition) return;

    // Sanity checks
    if (hasSchema && useRawTextarea && payload === undefined) {
      toast.error(t('transitionDialog.errors.invalidJson'));
      return;
    }
    if (hasSchema && !useRawTextarea && !formValid) {
      toast.error(t('transitionDialog.errors.submitFailed'));
      return;
    }

    try {
      await execute.mutateAsync({
        assetId,
        transitionCode: transition.code,
        payload: hasSchema ? payload : null,
      });
      toast.success(t('transitionDialog.success'));
      onOpenChange(false);
      onSuccess?.();
    } catch (e: any) {
      const data = e?.response?.data;
      const code = data?.code ?? 'ERROR';
      const message = data?.message ?? e?.message ?? t('transitionDialog.errors.submitFailed');
      const validNext: string[] = data?.validNext ?? [];
      const fieldErrors = data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const lines = Object.entries(fieldErrors)
          .map(([k, v]) => `${k}: ${v}`)
          .join('\n');
        toast.error(`${code}\n${lines}`);
      } else {
        toast.error(
          `${code}: ${message}${validNext.length ? ` — valid: ${validNext.join(', ')}` : ''}`
        );
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={hasSchema ? 'max-w-2xl' : ''}>
        <DialogHeader>
          <DialogTitle>
            {transition?.name ?? t('transitionDialog.title')}
            <Badge variant="outline" className="ml-2 font-mono text-xs">
              {transition?.code}
            </Badge>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 mt-1">
            <code className="text-xs">{assetId}</code>
            <ArrowRight className="h-3 w-3" />
            <code className="text-xs">{transition?.toStateCode}</code>
          </DialogDescription>
        </DialogHeader>

        {transition && !transition.allowed && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('stageActions.empty')}</AlertTitle>
            <AlertDescription>
              {transition.denyReasonCode ?? t('stageActions.empty')}
            </AlertDescription>
          </Alert>
        )}

        {hasSchema && schemaQuery.isLoading && <DynamicMetadataFormSkeleton />}

        {hasSchema && schemaFetchFailed && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('transitionDialog.formLoadFailed')}</AlertTitle>
          </Alert>
        )}

        {hasSchema && !schemaQuery.isLoading && schemaQuery.data && !useRawTextarea && (
          <div className="max-h-[60vh] overflow-y-auto">
            <DynamicMetadataForm
              schema={schemaQuery.data}
              values={values}
              onChange={setValues}
              onValidationChange={(isValid) => setFormValid(isValid)}
            />
            <div className="mt-2 text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRaw(true)}
                className="text-xs"
              >
                <Code2 className="h-3 w-3 mr-1" />
                {t('designer.jsonEscape')}
              </Button>
            </div>
          </div>
        )}

        {hasSchema && useRawTextarea && (
          <div className="space-y-2">
            <Label htmlFor="payload">
              {t('transitionDialog.jsonLabel')}
            </Label>
            <Textarea
              id="payload"
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              placeholder='{ "severity": "MAJOR", "reason": "..." }'
              className="font-mono text-xs min-h-[10rem]"
              spellCheck={false}
            />
            <p className="text-[11px] text-muted-foreground">{t('transitionDialog.jsonHint')}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('transitionDialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={execute.isPending || transition?.allowed === false}
          >
            {execute.isPending ? t('transitionDialog.submitting') : t('transitionDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
